import { FetchingButton, FieldSet, OtpField } from '@glade-ai/ui';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, type UseFormProps, useForm } from 'react-hook-form';

import getReturnToFromRouter from '@/components/Auth/helpers/getReturnToFromRouter';
import FieldError from '@/components/FieldError';
import Shimmer from '@/components/Shimmer';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import useIPGeolocation from '@/hooks/useIPGeolocation';
import useNoodleApi from '@/hooks/useNoodleApi';
import { getNextUrl } from '@/lib/Auth/useAfterAuth';
import { useUser } from '@/providers/Auth';
import { mixpanelAlias, mixpanelTrack } from '@/providers/Mixpanel';
import * as tsClient from '@/tsClient';

import requestOTPErrorMessage from '../helpers/requestOTPErrorMessage';

import s from './VerifyOTP.module.scss';

type Props = {
  authCodeLength: number;
  phoneNumber: string;
  recipient: string;
  country: string;
  creatorSlug?: string;
  canSendViaEmail: boolean;
  source?: string;
  isCreator?: boolean;
  hasFinishedOnboarding?: boolean;
};

type VerifyOTPSchema = { authCode: string };

const VerifyOTP: React.FC<Props> = ({
  authCodeLength,
  phoneNumber,
  recipient,
  country,
  creatorSlug,
  canSendViaEmail,
  source,
  isCreator,
  hasFinishedOnboarding,
}) => {
  const [_user, _setUser, setUserByToken] = useUser();
  const [sentTo, setSentTo] = useState(recipient);
  const [error, setError] = useState<string | null>(null);
  const [resendingVia, setResendingVia] = useState<'sms' | 'email' | null>(null);
  const router = useRouter();
  const returnTo = useMemo(
    () =>
      getReturnToFromRouter({
        returnToQuery: router.query.returnTo,
        routerAsPath: router.asPath,
        routerIsReady: router.isReady,
        routerPathname: router.pathname,
      }),
    [router.asPath, router.isReady, router.pathname, router.query.returnTo],
  );

  // react-hook-form setup similar to SignUpForm
  const formConfig = useMemo(
    () =>
      ({
        defaultValues: { authCode: '' },
        mode: 'onBlur',
      }) satisfies UseFormProps<VerifyOTPSchema>,
    [],
  );

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    setError: setFormError,
    clearErrors,
  } = useForm<VerifyOTPSchema>(formConfig);

  const {
    error: requestOTPError,
    fetchingState: requestOTPState,
    getData: requestOTP,
  } = useNoodleApi(tsClient.auth.requestOTP, {
    healthMonitor: { name: 'sign-in' },
    reportError: false,
  });
  const {
    error: verifyOTPError,
    fetchingState: verifyOTPState,
    getData: verifyOTP,
  } = useNoodleApi(tsClient.auth.verifyOTP, {
    healthMonitor: { name: 'sign-in' },
    reportError: false,
  });

  const [geolocation] = useIPGeolocation();

  const verifyCode = async (code: string): Promise<void> => {
    const codeToSend = code;
    setValue('authCode', ''); // without this the auto submit keeps firing on errors
    const response = await verifyOTP({
      code: codeToSend,
      country,
      creatorSlug: creatorSlug || undefined,
      hasFinishedOnboarding,
      ipAddress: geolocation?.ip || null,
      isCreator: isCreator ?? false,
      phoneNumber,
      returns: ['primaryUserWorkflowId'],
      to: phoneNumber,
    });

    if (response.data) {
      const { isExistingUser, token, primaryUserWorkflowId } = response.data;
      const currentUser = setUserByToken(token);

      if (currentUser) {
        const url = getNextUrl({
          creatorSlug,
          primaryUserWorkflowId: primaryUserWorkflowId || undefined,
          returnTo: returnTo || undefined,
          user: currentUser,
        });
        router.push(url);
      }

      mixpanelTrack('Logged In', {
        method: resendingVia === 'email' ? 'Email' : 'Phone',
        phoneNumber,
      });
      if (!isExistingUser) {
        mixpanelTrack('Created Account', {
          creationMethod: source || 'Normal Signup',
        });
        if (currentUser) {
          mixpanelAlias(currentUser.id);
        }
      }
    }
  };

  useEffect(() => {
    setSentTo(recipient);
  }, [recipient]);

  const handleResendCode = async (channel: 'sms' | 'email'): Promise<void> => {
    setResendingVia(channel);
    setValue('authCode', '');
    setError(null);
    const response = await requestOTP({ channel, phoneNumber });

    if (response.data) {
      setSentTo(response.data.recipient);
    } else if (response.error) {
      const msg = requestOTPErrorMessage(response.error);
      if (msg === '__SUPPORT__') {
        router.push(getUrl(IDENTIFIERS.SUPPORT));
      } else {
        setError(msg);
      }
    }

    setResendingVia(null);
  };

  const onSubmit = async ({ authCode }: VerifyOTPSchema): Promise<void> => {
    if (authCode.length !== authCodeLength) {
      setFormError('authCode', { message: 'Invalid code', type: 'validate' });
      setError('Invalid code');
      return;
    }
    await verifyCode(authCode);
  };

  // Auto-submit when code length reached
  const watchedCode = watch('authCode');
  useEffect(() => {
    // Clear any previous validation or API errors when user edits the code
    if (watchedCode !== undefined) {
      clearErrors('authCode');
      setError(null);
    }

    if (watchedCode && watchedCode.length === authCodeLength && !verifyOTPState.isFetching) {
      onSubmit({ authCode: watchedCode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedCode, authCodeLength]);

  const errorMessage =
    error ||
    (requestOTPError && (requestOTPError?.errors?.[0] || requestOTPError.message)) ||
    verifyOTPError?.message ||
    null;

  const isBusy = resendingVia !== null || requestOTPState.isFetching || verifyOTPState.isFetching;
  const isResendingSMS = resendingVia === 'sms';
  const isResendingEmail = resendingVia === 'email';

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FieldSet>
        {verifyOTPState.isFetching ? (
          <div className={s.shimmer}>
            <Shimmer rounded={false} />
          </div>
        ) : (
          <>
            <Controller
              control={control}
              name='authCode'
              rules={{
                maxLength: authCodeLength,
                minLength: authCodeLength,
                pattern: /^(\d+)$/,
                required: true,
              }}
              render={({ field, fieldState }) => (
                <OtpField
                  field={field}
                  fieldState={fieldState}
                  label={`Please enter the code we sent to ${sentTo}`}
                  maxLength={authCodeLength}
                  inputMode='numeric'
                />
              )}
            />
            {errorMessage && <FieldError>Error: {errorMessage}</FieldError>}
          </>
        )}
        <FetchingButton
          fetching={isResendingSMS}
          disabled={isBusy}
          onClick={() => handleResendCode('sms')}
          variant='secondary'
          size='sm'
          type='button'
        >
          Resend code
        </FetchingButton>
        {canSendViaEmail && (
          <FetchingButton
            fetching={isResendingEmail}
            disabled={isBusy}
            onClick={() => handleResendCode('email')}
            variant='secondary'
            size='sm'
            type='button'
          >
            Send to Email
          </FetchingButton>
        )}
        <FetchingButton size='lg' variant='primary' type='submit' fetching={isBusy} className='w-full'>
          Verify code
        </FetchingButton>
      </FieldSet>
    </form>
  );
};

export default VerifyOTP;
